"use client"

import { useState, useEffect, useRef } from "react"
import "./SoftwareProcess.css"

const ScopingProcess = () => {
  const [activeTab, setActiveTab] = useState("planning")
  const headerRef = useRef(null)
  const textRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    backlog: {
      title: "BACKLOG",
      subtitle1: "1. PRODUCT BACKLOG",
      subtitle2: "2. SPRINT BACKLOG",
      description:
        "The product backlog is a list of project goals and contains what is forecasted to be developed by the development team, and maintained by the Product Owner. It is a living document, updated continuously, prioritized, and ordered by business value. It may also have product improvements, bugs, technical questions, and so on. Its purpose is mainly to have everything that is needed to reach the project’s Product Vision.In this stage, we also create a sprint backlog, which is a list of tasks that need to be completed during each sprint. We prioritize the user stories for each sprint and ensure that the team knows what they need to work on.With the sprint backlog in place, the development process finally starts - sprint execution. Working through the sprint backlog, and delivering small usable pieces of software frequently allows for continuous feedback and refinement, ensuring that the product is always on the right track.",
      
    },
    bootstrap: {
      title: "BOOTSTRAP",
      subtitle1: "1. HIGH-LEVEL ARCHITECTURE",
      subtitle2: "2. BRIEFING",
      subtitle3: "3. CI/CD WORKFLOW",
      subtitle4: "4. FEATURE 0",
      description:
        "In this stage, we put in place a briefing that includes the information gathered during the workshop with the team and stakeholders. It presents the vision and goals of the project and clarifies all necessary business requirements. This is also where an FAQ session relative to the nature of the project takes place.The high-level architecture involves the development of the technical design, with the ideal balance between complexity and reach, This is where we identify external dependencies from third-party providers, such as Stripe, Facebook, Amazon, and so on.We then start the CI/CD workflow which is the setup of the issue management tool, code repositories, continuous integration system, and development & staging environments. It’s followed by the setup of the code repo and automated test framework, the staging environment and production servers, as well as the continuous integration ecosystem (i.e. servers, deploy hooks, etc)/continuous deployment.Finally, in Feature 0 we deliver the first meaningful feature: a homepage, a login screen, part of the first dashboard… This step ensures that there is something demonstrable with the perception of value at the end of the Bootstrap Phase.",
      
    },
    wave0: {
      title: "WAVE 0",
      subtitle1: "1. DATA MODEL",
      subtitle2: "2. POC,MTP OR MVP",
      subtitle3: "3. VIABILITY ASSESMENT",
      description:
        "With the Data Model, we provide the first baseline of the product’s evolutive data model. It identifies the main data entities and relationships and baselines the data sources and data stores (i.e. relational databases, document data stores, etc.). This step also consists of iterating the product concept and designing the first version of the data model.Here’s when we present the Proof Of Concept (PoC), Minimal Testable Product (MTP), or Minimal Viable Product (MVP), and we deliver and deploy the first version of the product - even if that version is the implementation of a concept. This helps mitigate technical risks and test the main business premises for developing a market-ready version through a viability assessment.",
      
    },

    wavein: {
      title: "WAVE-IN",
      subtitle1: "1. PRODUCTION INCREMENTS",
      subtitle2: "2. WAVE RETROSPECTIVE REVIEWS",
      description:
        "On the production increments step, we review technical and business risks and the impact of the PoC, MTP, or MVP on the initial premises or Wave 0. Here, we also identify reusable components from Wave 0 to Wave 1 (i.e. often PoCs are not reusable). This helps gather feedback about the first integrated model and assesses the product's viability before moving to Wave 1.Finally, the wave retrospective reviews the product state, evaluates wave success against business goals, and identifies improvements. We then design goals for the next wave & prioritize features. Doing this allows the team to analyze the work that has been done in previous sprints and plan consciously what should be the next mountain to climb.Create a detailed roadmap that will be used to identify the main features the product will have and prioritize them.",
     
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1 },
    )

    const elements = [headerRef.current, textRef.current, imageRef.current, tabsRef.current]
    elements.forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => {
      elements.forEach((el) => {
        if (el) observer.unobserve(el)
      })
    }
  }, [])

  return (
    <div className="scoping-container" id="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Header */}
        <div className="header-section2" ref={headerRef}>
          <h2 className="header-title2">
            OUR
            <br />
            <span className="highlight">ADP</span>
            <br />
            <span className="highlight">PROCESS</span>
          </h2>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="text-section" ref={textRef}>
            <p className="main-description">
              Throughout the software lifecycle, we use the Agile Development Process - a set of agile software principles and practices that emphasise cooperation, flexibility, and continuous development, allowing teams to deliver top-notch software in a timely and efficient manner. Consider agile methodology your software development GPS, giving guidance, navigation, and the ability to react to changes along the road.
            </p>
          </div>
          <div className="image-section" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Scoping Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "backlog" ? "active" : ""}`}
              onClick={() => setActiveTab("backlog")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 20h20M5 20V8.5M9 20v-9M13 20v-5.5M17 20v-9"></path>
                </svg>
              </div>
              BACKLOG
            </button>
            <button
              className={`tab-button ${activeTab === "bootstrap" ? "active" : ""}`}
              onClick={() => setActiveTab("bootstrap")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </div>
              BOOTSTRAP
            </button>
            <button
              className={`tab-button ${activeTab === "wave0" ? "active" : ""}`}
              onClick={() => setActiveTab("wave0")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              WAVE 0
            </button>
            <button
              className={`tab-button ${activeTab === "wavein" ? "active" : ""}`}
              onClick={() => setActiveTab("wavein")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 20h20M5 20V8.5M9 20v-9M13 20v-5.5M17 20v-9"></path>
                </svg>
              </div>
              WAVE IN
            </button>
          </div>

          <div className="tab-content1">
            <div className={`tab-panel ${activeTab === "backlog" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.backlog.title}</h3>
               <h4 className="tab-subtitle1">{tabContent.backlog.subtitle1}</h4>
              <h4 className="tab-subtitle1">{tabContent.backlog.subtitle2}</h4>
              <h4 className="tab-subtitle1">{tabContent.backlog.subtitle3}</h4>
              <p className="tab-description1">{tabContent.backlog.description}</p>
            </div>

            <div className={`tab-panel ${activeTab === "bootstrap" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.bootstrap.title}</h3>
               <h4 className="tab-subtitle1">{tabContent.bootstrap.subtitle1}</h4>
              <h4 className="tab-subtitle1">{tabContent.bootstrap.subtitle2}</h4>
              <h4 className="tab-subtitle1">{tabContent.bootstrap.subtitle3}</h4>
              <h4 className="tab-subtitle1">{tabContent.bootstrap.subtitle4}</h4>
              <p className="tab-description1">{tabContent.bootstrap.description}</p>
              
            </div>

            <div className={`tab-panel ${activeTab === "wave0" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.wave0.title}</h3>
              <h4 className="tab-subtitle1">{tabContent.wave0.subtitle1}</h4>
              <h4 className="tab-subtitle1">{tabContent.wave0.subtitle2}</h4>
              <h4 className="tab-subtitle1">{tabContent.wave0.subtitle3}</h4>
              <p className="tab-description1">{tabContent.wave0.description}</p>
              <p className="tab-description1">{tabContent.wave0.description}</p>
            </div>
            
              <div className={`tab-panel ${activeTab === "wavein" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.wavein.title}</h3>
              <h4 className="tab-subtitle1">{tabContent.wavein.subtitle1}</h4>
              <h4 className="tab-subtitle1">{tabContent.wavein.subtitle2}</h4>
              <p className="tab-description1">{tabContent.wavein.description}</p>
              
              
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our team of business developers and project managers are ready to analyze your questions. Have a coffee,
            free time to explain us everything and get a quote for your project.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
      </div>
    </div>
  )
}

export default ScopingProcess;
